import { ChangeDetectorRef, TemplateRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ContentType } from 'src/app/models/documents/folder.model';
import { DocumentService } from 'src/app/services/document.service';
import { FrontendLoggerService } from 'src/app/services/frontend-logger.service';
import { NotificationService } from 'src/app/services/notification.service';
import { DriveMoveExecutionCoordinator } from './drive-move-execution.coordinator';
import { DriveSelectionQuery } from './drive-selection.query';
import { DriveItem } from './drive-viewer.models';
import { DriveMoveState, DriveWorkspaceState } from './drive-viewer.state';

export class DriveContextMenuCoordinator {
  constructor(
    private readonly workspace: DriveWorkspaceState,
    private readonly state: DriveMoveState,
    private readonly selection: DriveSelectionQuery,
    private readonly moveExecution: DriveMoveExecutionCoordinator,
    private readonly documents: DocumentService,
    private readonly dialog: MatDialog,
    private readonly router: Router,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly notifications: NotificationService,
    private readonly logger: FrontendLoggerService,
    private readonly translate: (key: string) => string,
    private readonly myDriveLabel: () => string,
    private readonly refresh: (silent?: boolean) => void
  ) {}

  onRoot(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const root = this.selection.rootFolder();
    if (root) this.openFolder(event, root.id);
  }

  onTree(event: MouseEvent, item: DriveItem): void {
    event.preventDefault();
    event.stopPropagation();
    if (item.type !== 'folder') {
      this.openFile(event, item);
    } else if (!this.selection.isVirtualFolderId(item.id)) {
      this.openFolder(event, item.id);
    }
  }

  openFolder(event: MouseEvent, folderId: string): void {
    this.position(event);
    this.state.contextFolderId = folderId;
    this.state.contextMenuMode = 'folder';
    this.state.contextMenuItem = undefined;
    this.state.contextMoveExpanded = false;
    this.state.contextMenuVisible = true;
  }

  openFile(event: MouseEvent, item: DriveItem): void {
    this.position(event);
    this.state.contextFolderId = item.parentId ? String(item.parentId) : this.workspace.currentFolderId;
    this.state.contextMenuMode = 'file';
    this.state.contextMenuItem = item;
    this.state.contextMoveExpanded = false;
    if (!this.workspace.selectedIds.has(item.id)) {
      this.workspace.selectedIds.clear();
      this.workspace.selectedIds.add(item.id);
    }
    this.state.contextMenuVisible = true;
  }

  close(): void {
    this.state.contextMenuVisible = false;
    this.state.contextMenuItem = undefined;
    this.state.contextMoveExpanded = false;
  }

  openCreateFolderDialog(template: TemplateRef<unknown>): void {
    if (!this.state.contextFolderId) return;
    if (this.selection.isVirtualFolderId(this.state.contextFolderId)) {
      this.close();
      return;
    }
    this.state.currentDialogRef = this.dialog.open(template, {
      width: '420px',
      maxWidth: 'calc(100vw - 32px)',
      autoFocus: false,
      panelClass: 'drive-document-dialog-panel'
    });
    this.state.currentDialogRef.afterClosed().subscribe(() => {
      this.state.newFolderName = '';
      this.close();
      this.state.currentDialogRef = undefined;
    });
  }

  openCreateFolderFromFallback(): void {
    if (this.state.createFolderDialogTpl) this.openCreateFolderDialog(this.state.createFolderDialogTpl);
  }

  confirmCreateFolder(): void {
    const parentIdString = this.state.contextFolderId;
    const parentId = Number.parseInt(parentIdString || '', 10);
    if (!parentIdString || !this.state.newFolderName || Number.isNaN(parentId)) {
      this.state.currentDialogRef?.close();
      return;
    }
    this.documents.createFolder({
      name: this.state.newFolderName,
      parentId,
      contentType: ContentType.FOLDER,
      documentId: null
    }).subscribe({
      next: (created) => this.applyCreatedFolder(created, parentIdString),
      error: () => {
        this.state.currentDialogRef?.close();
        this.state.newFolderName = '';
      }
    });
  }

  askAboutContextFile(): void {
    const item = this.state.contextMenuItem;
    const documentId = this.moveExecution.resolveDocumentId(item);
    if (!item || !documentId) {
      this.close();
      return;
    }
    try {
      const safeName = String(item.name || '').replace(/\|/g, ' ');
      localStorage.setItem('limitChatDocId', `${documentId}|${safeName}`);
    } catch (error) {
      this.logger.warn('[drive-viewer] could not write selected document to localStorage: ' + String(error));
    }
    this.close();
    this.router.navigate(['/chat']);
  }

  deleteContextFile(template?: TemplateRef<unknown>): void {
    const item = this.state.contextMenuItem;
    if (!item || !this.moveExecution.resolveDocumentId(item)) {
      this.close();
      return;
    }
    this.state.deleteDialogItem = item;
    this.state.deleteDialogName = item.name;
    this.state.deleteDialogBusy = false;
    this.close();
    if (!template) {
      this.confirmDeleteDialog();
      return;
    }
    this.state.deleteDialogRef = this.dialog.open(template, {
      width: '420px', maxWidth: 'calc(100vw - 32px)', panelClass: 'drive-document-dialog-panel'
    });
  }

  confirmDeleteDialog(): void {
    const item = this.state.deleteDialogItem;
    const documentId = this.moveExecution.resolveDocumentId(item);
    if (!item || !documentId) {
      this.state.deleteDialogRef?.close();
      return;
    }
    this.state.deleteDialogBusy = true;
    this.documents.deleteDocument(documentId).subscribe({
      next: () => {
        this.workspace.items = this.workspace.items.filter((candidate) => {
          return candidate.id !== item.id && candidate.documentId !== documentId;
        });
        this.workspace.selectedIds.delete(item.id);
        this.finishDelete();
        this.refresh(true);
      },
      error: () => {
        this.logger.error(`[DriveViewerComponent] Error al eliminar documento ${documentId}`);
        this.finishDelete();
        this.notifications.showTransient(this.translate('documents.delete_confirm.error'), 'warning');
        this.refresh(true);
      }
    });
  }

  private applyCreatedFolder(created: any, parentId: string): void {
    const realId = this.moveExecution.extractCreatedFolderId(created);
    if (!Number.isFinite(realId)) {
      this.logger.error(`[DriveViewerComponent][createFolder] Respuesta sin ID válido: ${JSON.stringify(created)}`);
      this.state.currentDialogRef?.close();
      this.state.newFolderName = '';
      this.state.contextMenuVisible = false;
      this.workspace.expanded.add(parentId);
      this.refresh();
      return;
    }
    const parent = this.workspace.items.find((item) => item.id === parentId);
    const name = created?.name || this.state.newFolderName;
    const folder: DriveItem = {
      id: String(realId),
      name,
      type: 'folder',
      owner: 'you',
      modified: created?.createdAt || new Date().toISOString(),
      size: 0,
      path: [...(parent?.path || [this.myDriveLabel()]), name],
      parentId
    };
    const index = this.workspace.items.findIndex((item) => item.id === folder.id);
    index >= 0
      ? this.workspace.items[index] = { ...this.workspace.items[index], ...folder }
      : this.workspace.items.push(folder);
    this.workspace.expanded.add(parentId);
    this.state.currentDialogRef?.close();
    this.state.newFolderName = '';
    this.state.contextMenuVisible = false;
    this.changeDetector.detectChanges();
  }

  private finishDelete(): void {
    this.state.deleteDialogBusy = false;
    this.state.deleteDialogItem = undefined;
    this.state.deleteDialogRef?.close();
  }

  private position(event: MouseEvent): void {
    this.state.menuX = event.clientX;
    this.state.menuY = event.clientY;
  }
}
